import { Action, configureStore, EnhancedStore, Middleware, ThunkAction } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import { Firestore, DocumentData } from 'firebase/firestore';
import { Auth } from 'firebase/auth';
import { Functions, HttpsCallableResult } from 'firebase/functions';
import { Database } from 'firebase/database';
import { AnalyticsService } from '../services/AnalyticsService';
import { createRootReducer } from './reducers';
import { UserReducer } from './reducers/user/types';
import { NotificationsReducer } from './reducers/notifications/types';
import { SubscriptionReducer } from './reducers/subscription/types';
import { IntegrationApiTokensReducer } from './reducers/integrationApiTokens/types';
import { StatisticsReducer } from './reducers/statistics/types';

export interface AppStore {
  user: UserReducer;
  notifications: NotificationsReducer;
  statistics: StatisticsReducer;
  integrationApiTokens: IntegrationApiTokensReducer;
  subscription: SubscriptionReducer;
}

export interface StoreDependencies {
  firestore: () => Firestore;
  db: Firestore;
  auth: () => Auth;
  analytics: AnalyticsService;
  functions: Functions;
  database?: () => Database;
  config?: {
    APP_FUNCTION_DOMAIN: string;
  };
}

type UserDetailsListener = {
  userDetailsListener?: Function;
  subscriptionListener?: Function;
  currentUserDetailsUid?: string;
};

type NotificationsListener = {
  notificationsListener?: Function;
};

type IntegrationListener = {
  integrationListener?: Function;
  apiTokenListener?: Function;
};

type NewslettersListener = {
  newslettersListener?: Function;
};

type NewsletterListener = {
  newsletterListener?: Function;
};

type ArchiveListener = {
  archiveListener?: Function;
  lastArchiveDocumentCursor?: DocumentData | null;
};

type ProductAutomationListener = {
  productAutomationListener?: Function;
};

type ReportsListener = {
  reportsListener?: Function;
  isCreatorStatsSubscribed?: boolean;
};

type ProductReferralsListener = {
  referralsListener?: Function;
};

type UserReferralsListener = {
  userReferralsListener?: Function;
};

type DiscountsListener = {
  discountsListener?: Function;
};

type ProductUserSettingsListener = {
  productUserSettingsListener?: Function;
};

type FirestoreSubscriptions = UserDetailsListener &
  NotificationsListener &
  IntegrationListener &
  NewsletterListener &
  NewslettersListener &
  ArchiveListener &
  ProductAutomationListener &
  ReportsListener &
  ProductReferralsListener &
  UserReferralsListener &
  DiscountsListener &
  ProductUserSettingsListener;

export interface FirestoreUtils {
  firestoreSubscriptions: FirestoreSubscriptions;
}

export type ActionParams<T = undefined, R = undefined> = {
  payload: T;
  onSuccess?: (_data?: R) => void;
  onFailure?: (errorCode?: number | string) => void;
};

export type AsyncThunkCreator<T = null> = {
  extra: StoreDependencies & FirestoreUtils;
  state: AppStore;
  rejectValue: T;
};

export type AppThunk<T = void> = ThunkAction<
  T,
  AppStore,
  StoreDependencies & FirestoreUtils,
  Action<string>
>;

export const createStore = (storeDependencies: StoreDependencies): { store: EnhancedStore } => {
  const middleware: Middleware[] = [];
  const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

  if (IS_DEVELOPMENT) {
    middleware.push(createLogger({ collapsed: true }) as Middleware);
  }
  const store = configureStore({
    reducer: createRootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
        thunk: {
          extraArgument: {
            ...storeDependencies,
            firestoreSubscriptions: {},
          },
        },
      }).concat(middleware),
  });

  return { store };
};
